import { useCallback } from "react";
import { stringify as queryStringStringify } from "qs";
import { generatePath as reactRouterGeneratePath, Params } from "react-router";

function getTopLevelDomain() {
  const { origin } = window?.location;
  const urlObj = new URL(origin);
  const domain = urlObj.hostname;
  const splitedDomain = domain.split(".");
  const tld =
    Array.isArray(splitedDomain) && splitedDomain.length > 1
      ? splitedDomain.pop().toLocaleLowerCase()
      : undefined;
  return tld;
}

const ROUTABLE_LOCALES = ["fr", "nl"];

/**
 * The locale segment of the URL on show, which is not the document's locale:
 * Rails routes accept `fr` and `nl` only, while `<html lang>` carries a full
 * locale such as `fr-BE`. Reading it from the path is also what keeps a
 * generated URL on the form the visitor arrived on.
 */
function getPathLocale() {
  const [firstSegment] = (window?.location?.pathname || "")
    .split("/")
    .filter((item) => item);

  return ROUTABLE_LOCALES.includes(firstSegment) ? firstSegment : undefined;
}

function removeLocalFromPath(path) {
  const splitPath = path && path.split("/").filter((item) => item);

  // checks if it has locale and tries to remove it
  if (
    Array.isArray(splitPath) &&
    ROUTABLE_LOCALES.filter((item) => item === splitPath[0]).length > 0
  )
    return `/${splitPath.slice(1).join("/")}`;
  return path; // if there is no local attached to the path
}

export type GeneratePath = (
  path: string,
  params?: Params,
  queryParams?: Params
) => string;

type IUsePath = () => { generatePath: GeneratePath };

export const usePath: IUsePath = () => {
  const generatePath: GeneratePath = useCallback(
    (path, params, queryParams) => {
      // A route asked for with a locale drops the segment when the URL on show
      // has none, rather than generating an unroutable `/undefined/...`.
      const locale = getPathLocale();
      let resolvedPath = reactRouterGeneratePath(
        locale ? path : path.replace(/^\/:locale(?=\/|$)/, ""),
        { locale, ...params }
      );

      /**
       * checks if domain is france origin and removes /fr on and route.
       * example: '/fr/listworker-rules' & "/fr/supertalent" will be "/listworker-rules" and "/supertalent"
       */
      const topLevelDomain = getTopLevelDomain();
      if (topLevelDomain === "fr") {
        resolvedPath = removeLocalFromPath(resolvedPath);
      }

      if (!queryParams) return resolvedPath;

      const { anchor, ...urlQueryParams } = queryParams;

      const queryString = queryStringStringify(urlQueryParams);

      let result = `${resolvedPath}?${queryString}`;
      result = anchor ? `${result}#${anchor}` : result;
      return result;
    },
    []
  );

  return { generatePath };
};
